import React, { FC, useEffect, useMemo, useState } from "react";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { Colors } from "../../../configs";
import { formatCurrency } from "../../../util/common-functions";
import { Link } from "react-router-dom";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import {
  deleteDraftAction,
  getCashierDraftsAction,
} from "../../../redux/actions/cartAction";
import { ICustomerResponse } from "../../../typings";
import {
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
} from "date-fns";

interface IDraftDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  forCustomer?: number;
  currentCustomerId?: number;
}

interface IDraftCardProps {
  id: number;
  createdAt: Date | string;
  sessionCount: number;
  totalPieces: number;
  totalAmount: number;
  customer: ICustomerResponse;
  draftSessions: any[];
  onPick?: () => void;
  canPick: boolean;
  isRecent?: boolean;
}

const timeAgo = (createdAt: Date | string) => {
  const now = new Date();
  const when = new Date(createdAt);
  const secs = Math.max(0, differenceInSeconds(now, when));
  if (secs < 45) return "just now";

  const mins = differenceInMinutes(now, when);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;

  const hrs = differenceInHours(now, when);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;

  const days = differenceInDays(now, when);
  if (days === 1) return "yesterday";
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const getInitials = (customer: ICustomerResponse) => {
  const parts = [customer?.firstName, customer?.lastName ?? customer?.middleName]
    .filter(Boolean)
    .map((s) => s![0]);
  return parts.slice(0, 2).join("").toUpperCase() || "?";
};

/* ── Stat chip inside each card ── */
const StatBox: FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Box sx={{ borderRadius: "10px", px: "10px", py: "8px", backgroundColor: "#EBEBEB" }}>
    <Typography
      sx={{
        fontSize: "10px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: Colors.ACCENT_GRAY_DISABLED,
        lineHeight: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: "4px",
        fontSize: "13px",
        fontWeight: 700,
        color: Colors.TEXT_GRAY_DARK,
        lineHeight: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </Typography>
  </Box>
);

/* ── Individual draft card ── */
const DraftCard: FC<IDraftCardProps> = ({
  id,
  createdAt,
  sessionCount,
  totalPieces,
  totalAmount,
  customer,
  draftSessions,
  onPick,
  canPick,
  isRecent = false,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const handleOnItemClick = async (e: React.MouseEvent) => {
    if (!canPick) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onPick?.();
    dispatch(CartSliceAction.clearSession());
    dispatch(CartSliceAction.setSelectedCustomer(customer));
    dispatch(CartSliceAction.setCartFromDraft(draftSessions));
    try {
      await dispatch(deleteDraftAction(id)).unwrap();
      if (user?.id) dispatch(getCashierDraftsAction(user.id));
    } catch (err) {
      console.error("Failed to delete draft", err);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await dispatch(deleteDraftAction(id)).unwrap();
      if (user?.id) dispatch(getCashierDraftsAction(user.id));
      if (window.location.pathname.includes("/pos/lobby")) {
        window.location.reload();
      }
    } catch (err) {
      console.error("Failed to delete draft", err);
    }
  };

  const As: any = canPick ? Link : "div";
  const linkProps = canPick ? { to: `/pos/${customer?.id}` } : {};

  return (
    <Box
      component={As}
      {...linkProps}
      onClick={handleOnItemClick}
      role="button"
      aria-disabled={!canPick}
      sx={{
        textDecoration: "none",
        color: "inherit",
        background: Colors.WHITE,
        borderRadius: "16px",
        p: "14px",
        border: `1px solid ${isRecent ? Colors.PRIMARY : Colors.ACCENT_GRAY}`,
        borderLeft: isRecent ? `4px solid ${Colors.PRIMARY}` : `1px solid ${Colors.ACCENT_GRAY}`,
        boxShadow: isRecent
          ? "0 4px 14px rgba(0,0,0,0.10)"
          : "0 1px 4px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        opacity: canPick ? 1 : 0.45,
        cursor: canPick ? "pointer" : "not-allowed",
        transition: "box-shadow 0.2s ease, opacity 0.2s ease",
        "&:hover": canPick
          ? {
              boxShadow: isRecent
                ? "0 6px 18px rgba(0,0,0,0.13)"
                : "0 3px 10px rgba(0,0,0,0.10)",
            }
          : undefined,
      }}
    >
      {/* Row 1: avatar + name + status pill */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "8px",
          flexWrap: "nowrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          {/* Initials avatar */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: Colors.SECONDARY,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: Colors.WHITE }}>
              {getInitials(customer)}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: "14px", fontWeight: 700, color: Colors.TEXT_GRAY_DARK, lineHeight: 1.3 }}
            >
              {customer?.firstName} {customer?.lastName ?? customer?.middleName}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: "11px", color: Colors.ACCENT_GRAY_DISABLED, mt: "2px" }}
            >
              {customer?.mobilePhone}
            </Typography>
          </Box>
        </Box>

        {/* Status pill — green tint for most recent, neutral for pending */}
        <Box
          sx={{
            flexShrink: 0,
            borderRadius: "20px",
            px: "9px",
            py: "4px",
            backgroundColor: isRecent ? "#EEF8F3" : Colors.BACKGROUND_GRAY,
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: isRecent ? Colors.PRIMARY : Colors.ACCENT_GRAY_DISABLED,
              whiteSpace: "nowrap",
            }}
          >
            {isRecent ? "Most recent" : "Pending"}
          </Typography>
        </Box>
      </Box>

      {/* Row 2: stat chips */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px" }}>
        <StatBox label="Sessions" value={sessionCount} />
        <StatBox label="Pieces" value={totalPieces} />
        <StatBox label="Amount" value={formatCurrency({ amount: totalAmount, prefix: "$" })} />
      </Box>

      {/* Row 3: time + action buttons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <AccessTimeIcon sx={{ fontSize: "12px", color: Colors.ACCENT_GRAY_DISABLED }} />
          <Typography sx={{ fontSize: "11px", color: Colors.ACCENT_GRAY_DISABLED }}>
            {timeAgo(createdAt)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <IconButton
            onClick={handleDelete}
            size="small"
            aria-label="Delete draft"
            sx={{
              p: "5px",
              color: Colors.BROWN,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#FDF0F0" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: "17px" }} />
          </IconButton>

          {canPick && (
            <Button
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: "13px !important" }} />}
              sx={{
                borderRadius: "10px",
                px: "11px",
                py: "5px",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "none",
                lineHeight: 1,
                backgroundColor: Colors.PRIMARY,
                color: Colors.WHITE,
                boxShadow: "none",
                "&:hover": { backgroundColor: Colors.PRIMARY_DARK, boxShadow: "none" },
              }}
            >
              Retrieve
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/* ── Drawer shell ── */
const DraftDrawer: FC<IDraftDrawerProps> = ({
  isOpen,
  toggleDrawer,
  forCustomer,
  currentCustomerId,
}) => {
  const { getCashierDrafts } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (user?.id && isOpen) {
      dispatch(getCashierDraftsAction(user.id)).unwrap();
    }
  }, [dispatch, isOpen, user?.id]);

  const filtered = useMemo(() => {
    const data = getCashierDrafts ?? [];
    return forCustomer
      ? data.filter((d: any) => d?.customer?.id === forCustomer)
      : data;
  }, [getCashierDrafts, forCustomer]);

  return (
    <CustomDrawer
      isOpen={isOpen}
      toggleDrawer={toggleDrawer}
      sx={{
        /* Override the 300px fixed width and default padding from CustomDrawer */
        width: "clamp(320px, 38vw, 460px)",
        padding: 0,
        background: Colors.BACKGROUND_GRAY,
        minHeight: "100%",
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* ── Header ── */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: Colors.WHITE,
            borderBottom: `1px solid ${Colors.ACCENT_GRAY}`,
            px: { xs: "14px", sm: "20px" },
            pt: "18px",
            pb: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            {/* Title block */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "5px" }}>
                <ShoppingBagOutlinedIcon sx={{ fontSize: "15px", color: Colors.PRIMARY }} />
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    color: Colors.PRIMARY,
                  }}
                >
                  Draft Orders
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: "18px", sm: "22px" },
                  fontWeight: 700,
                  color: Colors.TEXT_GRAY_DARK,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                Unfinished orders
              </Typography>
              <Typography
                sx={{
                  mt: "4px",
                  fontSize: "11px",
                  color: Colors.ACCENT_GRAY_DISABLED,
                  lineHeight: 1.5,
                }}
              >
                Complete all pending orders before logging out.
              </Typography>
            </Box>

            {/* Close button */}
            <IconButton
              onClick={toggleDrawer}
              aria-label="Close drafts"
              size="small"
              sx={{
                flexShrink: 0,
                borderRadius: "10px",
                color: Colors.MAIN_GRAY,
                "&:hover": { backgroundColor: Colors.BACKGROUND_GRAY },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Summary banner — PRIMARY green, matches lobby/login CTA green */}
          <Box
            sx={{
              mt: "12px",
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${Colors.PRIMARY} 0%, ${Colors.PRIMARY_DARK} 100%)`,
              px: "14px",
              py: "11px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  color: "rgba(255,255,255,0.70)",
                }}
              >
                Open draft count
              </Typography>
              <Typography
                sx={{
                  mt: "2px",
                  fontSize: "26px",
                  fontWeight: 700,
                  color: Colors.WHITE,
                  lineHeight: 1,
                }}
              >
                {filtered?.length ?? 0}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                backgroundColor: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.20)",
                borderRadius: "20px",
                px: "11px",
                py: "6px",
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: "14px", color: Colors.WHITE }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: Colors.WHITE, whiteSpace: "nowrap" }}>
                Review before logout
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Card list ── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            px: { xs: "12px", sm: "16px" },
            py: "14px",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {filtered?.map((draft: any, index: number) => (
            <DraftCard
              key={draft.id}
              id={draft.draftId}
              customer={draft.customer}
              createdAt={draft.createdAt}
              sessionCount={draft.draftSessions?.length ?? 0}
              totalPieces={draft.totalPieces ?? 0}
              totalAmount={draft.totalAmount ?? 0}
              draftSessions={draft.draftSessions ?? []}
              onPick={toggleDrawer}
              canPick={
                currentCustomerId == null
                  ? true
                  : draft.customer?.id === currentCustomerId
              }
              isRecent={index === 0}
            />
          ))}

          {(filtered?.length ?? 0) === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: "48px",
              }}
            >
              <Typography
                sx={{ fontSize: "13px", color: Colors.ACCENT_GRAY_DISABLED, textAlign: "center" }}
              >
                No drafts found
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </CustomDrawer>
  );
};

export default DraftDrawer;
